import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calculator,
  FileText,
  IndianRupee,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import RadioGroup from '@ui/RadioGroup';
import { ResultSummaryCard } from '@ui/ResultSummaryCard';
import Toggle from '@ui/Toggle';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  formatCurrency,
  formatIndianNumberShort,
} from '@utils/numberFormatter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const SIPCalculator = () => {
  // SIP Calculator State
  const [monthlyInvestment, setMonthlyInvestment] = useState<number | null>(
    5000
  );
  const [lumpsumAmount, setLumpsumAmount] = useState<number | null>(0);
  const [expectedReturn, setExpectedReturn] = useState<number | null>(12);
  const [timePeriod, setTimePeriod] = useState<number | null>(10);
  const [stepUpEnabled, setStepUpEnabled] = useState<boolean>(false);
  const [stepUpType, setStepUpType] = useState<'percentage' | 'absolute'>(
    'percentage'
  );
  const [stepUpValue, setStepUpValue] = useState<number | null>(10);
  const [results, setResults] = useState({
    totalInvestment: 0,
    lumpsumInvestment: 0,
    estimatedReturns: 0,
    maturityValue: 0,
  });

  // Calculate SIP + Lumpsum returns with step-up
  const calculateSIP = useCallback(() => {
    // Handle null values by using defaults
    const monthlyInv = monthlyInvestment || 0;
    const lumpsum = lumpsumAmount || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;
    const stepUp = stepUpValue || 0;

    const monthlyRate = returnRate / 100 / 12;
    let totalSIPInvestment = 0;
    let sipMaturityValue = 0;

    if (stepUpEnabled && stepUp > 0) {
      // Calculate step-up SIP year by year
      let currentMonthlyAmount = monthlyInv;

      for (let year = 1; year <= period; year++) {
        const yearlyInvestment = currentMonthlyAmount * 12;
        totalSIPInvestment += yearlyInvestment;

        // Calculate maturity value for this year's monthly investments
        for (let month = 1; month <= 12; month++) {
          const monthsToMaturity = (period - year) * 12 + (12 - month + 1);
          const monthlyMaturityValue =
            currentMonthlyAmount * Math.pow(1 + monthlyRate, monthsToMaturity);
          sipMaturityValue += monthlyMaturityValue;
        }

        // Step up for next year
        if (year < period) {
          if (stepUpType === 'percentage') {
            currentMonthlyAmount = currentMonthlyAmount * (1 + stepUp / 100);
          } else {
            currentMonthlyAmount = currentMonthlyAmount + stepUp;
          }
        }
      }
    } else {
      // Regular SIP calculation
      const totalMonths = period * 12;
      totalSIPInvestment = monthlyInv * totalMonths;

      if (monthlyRate === 0) {
        // When return rate is 0%, maturity value equals total investment
        sipMaturityValue = totalSIPInvestment;
      } else {
        sipMaturityValue =
          monthlyInv *
          (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
            (1 + monthlyRate));
      }
    }

    // Lumpsum formula: A = P(1 + r)^t
    const lumpsumMaturityValue =
      lumpsum * Math.pow(1 + returnRate / 100, period);

    const totalInvestment = totalSIPInvestment + lumpsum;
    const maturityValue = sipMaturityValue + lumpsumMaturityValue;
    const estimatedReturns = maturityValue - totalInvestment;

    setResults({
      totalInvestment: totalSIPInvestment,
      lumpsumInvestment: lumpsum,
      estimatedReturns,
      maturityValue,
    });
  }, [
    monthlyInvestment,
    lumpsumAmount,
    expectedReturn,
    timePeriod,
    stepUpEnabled,
    stepUpType,
    stepUpValue,
  ]);

  // Generate chart data for investment vs value comparison
  const generateChartData = () => {
    // Handle null values by using defaults
    const monthlyInv = monthlyInvestment || 0;
    const lumpsum = lumpsumAmount || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;
    const stepUp = stepUpValue || 0;

    const years = [];
    const totalInvestedData = [];
    const portfolioValueData = [];

    const monthlyRate = returnRate / 100 / 12;
    const annualRate = returnRate / 100;

    // Add starting point (Today)
    years.push('Today');
    totalInvestedData.push(lumpsum);
    portfolioValueData.push(lumpsum);

    let cumulativeSIPInvested = 0;
    let cumulativeSIPValue = 0;
    let currentMonthlyAmount = monthlyInv;

    for (let year = 1; year <= period; year++) {
      if (stepUpEnabled && stepUp > 0) {
        // Step-up SIP calculation
        const yearlyInvestment = currentMonthlyAmount * 12;
        cumulativeSIPInvested += yearlyInvestment;

        // Calculate cumulative SIP value up to this year
        cumulativeSIPValue = 0;
        let tempMonthlyAmount = monthlyInv;

        for (let y = 1; y <= year; y++) {
          for (let m = 1; m <= 12; m++) {
            const monthsFromStart = (y - 1) * 12 + m;
            const monthsToEnd = year * 12 - monthsFromStart + 1;
            cumulativeSIPValue +=
              tempMonthlyAmount * Math.pow(1 + monthlyRate, monthsToEnd);
          }

          if (y < year && stepUp > 0) {
            if (stepUpType === 'percentage') {
              tempMonthlyAmount = tempMonthlyAmount * (1 + stepUp / 100);
            } else {
              tempMonthlyAmount = tempMonthlyAmount + stepUp;
            }
          }
        }

        // Step up for next year
        if (year < period) {
          if (stepUpType === 'percentage') {
            currentMonthlyAmount = currentMonthlyAmount * (1 + stepUp / 100);
          } else {
            currentMonthlyAmount = currentMonthlyAmount + stepUp;
          }
        }
      } else {
        // Regular SIP calculation
        const months = year * 12;
        cumulativeSIPInvested = monthlyInv * months;

        if (monthlyRate === 0) {
          // When return rate is 0%, SIP value equals total invested
          cumulativeSIPValue = cumulativeSIPInvested;
        } else {
          cumulativeSIPValue =
            monthlyInv *
            (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
              (1 + monthlyRate));
        }
      }

      const totalInvested = cumulativeSIPInvested + lumpsum;

      // Lumpsum growth over time
      const lumpsumValue = lumpsum * Math.pow(1 + annualRate, year);

      // Combined portfolio value
      const totalPortfolioValue = cumulativeSIPValue + lumpsumValue;

      years.push(`Year ${year}`);
      totalInvestedData.push(totalInvested);
      portfolioValueData.push(totalPortfolioValue);
    }

    return {
      labels: years,
      datasets: [
        {
          label: 'Total amount invested',
          data: totalInvestedData,
          borderColor: COLORS.accent,
          backgroundColor: `${COLORS.accent}20`,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: COLORS.accent,
          pointBorderColor: COLORS.accent,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Portfolio value',
          data: portfolioValueData,
          borderColor: COLORS.sage,
          backgroundColor: `${COLORS.sage}20`,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: COLORS.sage,
          pointBorderColor: COLORS.sage,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const generateTableData = () => {
    // Handle null values by using defaults
    const monthlyInv = monthlyInvestment || 0;
    const lumpsum = lumpsumAmount || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;
    const stepUp = stepUpValue || 0;

    const tableData = [];
    const monthlyRate = returnRate / 100 / 12;
    const annualRate = returnRate / 100;

    // Add initial row (Today/Beginning) if there's a lumpsum
    if (lumpsum > 0) {
      tableData.push({
        year: 'Today',
        isInitial: true,
        monthlyAmount: 0,
        yearlyInvestment: 0,
        cumulativeSIPInvested: 0,
        lumpsumInvested: lumpsum,
        lumpsumValue: lumpsum,
        lumpsumGains: 0,
        totalInvested: lumpsum,
        totalPortfolioValue: lumpsum,
        totalGains: 0,
      });
    }

    let cumulativeSIPInvested = 0;
    let cumulativeSIPValue = 0;
    let currentMonthlyAmount = monthlyInv;

    for (let year = 1; year <= period; year++) {
      let yearlyInvestment = 0;

      if (stepUpEnabled && stepUp > 0) {
        // Step-up SIP calculation
        yearlyInvestment = currentMonthlyAmount * 12;
        cumulativeSIPInvested += yearlyInvestment;

        // Calculate cumulative SIP value up to this year
        cumulativeSIPValue = 0;
        let tempMonthlyAmount = monthlyInv;

        for (let y = 1; y <= year; y++) {
          for (let m = 1; m <= 12; m++) {
            const monthsFromStart = (y - 1) * 12 + m;
            const monthsToEnd = year * 12 - monthsFromStart + 1;
            cumulativeSIPValue +=
              tempMonthlyAmount * Math.pow(1 + monthlyRate, monthsToEnd);
          }

          if (y < year && stepUp > 0) {
            if (stepUpType === 'percentage') {
              tempMonthlyAmount = tempMonthlyAmount * (1 + stepUp / 100);
            } else {
              tempMonthlyAmount = tempMonthlyAmount + stepUp;
            }
          }
        }

        // Step up for next year
        if (year < period) {
          if (stepUpType === 'percentage') {
            currentMonthlyAmount = currentMonthlyAmount * (1 + stepUp / 100);
          } else {
            currentMonthlyAmount = currentMonthlyAmount + stepUp;
          }
        }
      } else {
        // Regular SIP calculation
        yearlyInvestment = monthlyInv * 12;
        const months = year * 12;
        cumulativeSIPInvested = monthlyInv * months;

        if (monthlyRate === 0) {
          // When return rate is 0%, SIP value equals total invested
          cumulativeSIPValue = cumulativeSIPInvested;
        } else {
          cumulativeSIPValue =
            monthlyInv *
            (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
              (1 + monthlyRate));
        }
      }

      // Lumpsum growth over time
      const lumpsumValue = lumpsum * Math.pow(1 + annualRate, year);
      const lumpsumGains = lumpsumValue - lumpsum;
      const totalInvested = cumulativeSIPInvested + lumpsum;
      const totalPortfolioValue = cumulativeSIPValue + lumpsumValue;
      const totalGains = totalPortfolioValue - totalInvested;

      tableData.push({
        year,
        isInitial: false,
        monthlyAmount:
          stepUpEnabled && stepUp > 0
            ? year === 1
              ? monthlyInv
              : stepUpType === 'percentage'
                ? monthlyInv * Math.pow(1 + stepUp / 100, year - 1)
                : monthlyInv + stepUp * (year - 1)
            : monthlyInv,
        yearlyInvestment,
        cumulativeSIPInvested,
        lumpsumInvested: 0, // No additional lumpsum investment in subsequent years
        lumpsumValue,
        lumpsumGains,
        totalInvested,
        totalPortfolioValue,
        totalGains,
      });
    }

    return tableData;
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: COLORS.accent,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            const value = `₹${context.parsed.y.toLocaleString('en-IN')}`;
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.smoke,
        },
      },
      y: {
        grid: {
          color: `${COLORS.smoke}20`,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.charcoal,
          callback: function (value: any) {
            return `₹${value.toLocaleString('en-IN')}`;
          },
        },
        title: {
          display: true,
          text: 'Amount (₹)',
          color: COLORS.charcoal,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  // Recalculate when inputs change
  useEffect(() => {
    calculateSIP();
  }, [calculateSIP]);

  return (
    <ToolLayout
      title={`SIP calculator | ${CONTACT_INFO.company.name}`}
      heroTitle="SIP calculator"
      description="Calculate your Systematic Investment Plan (SIP) returns and see how your money grows over time"
      canonicalUrl="https://niveshright.com/tools/sip-calculator"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <Calculator size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="SIP calculator, systematic investment plan, mutual fund calculator, SIP returns, step up SIP, investment calculator, financial planning, SIP returns calculator"
      cta={
        <WhatsAppCTA
          title="Ready to start your SIP journey?"
          description="Get personalized investment advice and start your SIP with expert guidance."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}! I used your SIP calculator with these details:

Monthly SIP: ${formatCurrency(monthlyInvestment || 0)}${
            lumpsumAmount
              ? `
Lumpsum: ${formatCurrency(lumpsumAmount)}`
              : ''
          }${
            stepUpEnabled && stepUpValue
              ? `
Step-up: ${stepUpValue}${stepUpType === 'percentage' ? '%' : ''} annually`
              : ''
          }
Investment period: ${timePeriod || 0} years
Expected return: ${expectedReturn || 0}% p.a.

CALCULATION RESULTS:
• Total investment: ₹${(results.totalInvestment + results.lumpsumInvestment).toLocaleString('en-IN')}
• Maturity value: ₹${results.maturityValue.toLocaleString('en-IN')}
• Estimated returns: ₹${results.estimatedReturns.toLocaleString('en-IN')}
• Return multiple: ${(results.maturityValue / (results.totalInvestment + results.lumpsumInvestment)).toFixed(2)}x

I'd like personalized advice to start this SIP investment. Can you help me choose the right mutual funds and get started?`}
          buttonText="Get started with SIP"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.accent}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2
            className="text-2xl font-bold mb-8"
            style={{ color: COLORS.primary }}
          >
            Investment details
          </h2>

          {/* Initial Lumpsum Investment */}
          <NumberInputWithSlider
            label="Initial lumpsum amount (optional)"
            value={lumpsumAmount}
            onChange={setLumpsumAmount}
            min={0}
            max={10000000000}
            step={10000}
            placeholder="0"
            prefix="₹"
            showTooltip={true}
            minLabel="₹0"
            maxLabel={`₹${formatIndianNumberShort(10000000000, false)}`}
            className="mb-8"
          />

          {/* Monthly Investment */}
          <NumberInputWithSlider
            label="Monthly investment amount"
            value={monthlyInvestment}
            onChange={setMonthlyInvestment}
            min={500}
            max={1000000000}
            step={500}
            placeholder="5000"
            prefix="₹"
            showTooltip={true}
            minLabel="₹500"
            maxLabel={`₹${formatIndianNumberShort(1000000000, false)}`}
            className="mb-8"
          />

          {/* Expected Return */}
          <NumberInputWithSlider
            label="Expected annual return (%)"
            value={expectedReturn}
            onChange={setExpectedReturn}
            min={1}
            max={100}
            step={0.5}
            placeholder="12"
            minLabel="1%"
            maxLabel="100%"
            className="mb-8"
          />

          {/* Time Period */}
          <NumberInputWithSlider
            label="Investment period (years)"
            value={timePeriod}
            onChange={setTimePeriod}
            min={1}
            max={85}
            step={1}
            placeholder="10"
            minLabel="1 year"
            maxLabel="85 years"
            className="mb-8"
          />

          {/* Step-up SIP Option */}
          <Toggle
            label="Enable step-up SIP"
            checked={stepUpEnabled}
            onChange={setStepUpEnabled}
            className="mb-6"
          />

          {stepUpEnabled && (
            <div className="space-y-6 p-6 bg-gray-50 rounded-xl mb-8">
              {/* Step-up Type */}
              <RadioGroup
                label="Step-up type"
                options={[
                  {
                    value: 'percentage',
                    label: 'Percentage (%)',
                    description: 'Increase by % annually',
                  },
                  {
                    value: 'absolute',
                    label: 'Fixed amount (₹)',
                    description: 'Increase by ₹ annually',
                  },
                ]}
                value={stepUpType}
                onChange={value =>
                  setStepUpType(value as 'percentage' | 'absolute')
                }
                className="mb-0"
              />

              {/* Step-up Value */}
              <NumberInputWithSlider
                label={`Annual step-up ${stepUpType === 'percentage' ? 'percentage' : 'amount'}`}
                value={stepUpValue}
                onChange={setStepUpValue}
                min={0}
                max={stepUpType === 'percentage' ? 100 : 10000000}
                step={stepUpType === 'percentage' ? 1 : 100}
                placeholder="0"
                prefix={stepUpType === 'absolute' ? '₹' : undefined}
                showTooltip={stepUpType === 'absolute'}
                minLabel={stepUpType === 'percentage' ? '0%' : '₹0'}
                maxLabel={
                  stepUpType === 'percentage'
                    ? '100%'
                    : `₹${formatIndianNumberShort(10000000, false)}`
                }
                className="mb-0"
              />
            </div>
          )}
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-6">
            <ResultSummaryCard
              title="Total investment"
              value={
                <LocalizedCurrency
                  value={results.totalInvestment + results.lumpsumInvestment}
                />
              }
              subtitle={
                <>
                  SIP: <LocalizedCurrency value={results.totalInvestment} />
                  {results.lumpsumInvestment > 0 && (
                    <>
                      {' • Lumpsum: '}
                      <LocalizedCurrency value={results.lumpsumInvestment} />
                    </>
                  )}
                </>
              }
              icon={IndianRupee}
              iconColor={COLORS.accent}
              valueColor={COLORS.accent}
              delay={0.6}
            />

            <ResultSummaryCard
              title="Estimated returns"
              value={<LocalizedCurrency value={results.estimatedReturns} />}
              subtitle="Wealth gained from investments"
              icon={TrendingUp}
              iconColor={COLORS.sage}
              valueColor={COLORS.sage}
              delay={0.7}
            />

            <ResultSummaryCard
              title="Maturity value"
              value={<LocalizedCurrency value={results.maturityValue} />}
              subtitle={`Total corpus after ${timePeriod} years`}
              icon={Target}
              iconColor={COLORS.gold}
              valueColor={COLORS.gold}
              delay={0.8}
            />
          </div>

          {/* Growth Chart */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <div className="flex items-center mb-6">
              <BarChart3
                size={24}
                style={{ color: COLORS.gold }}
                className="mr-3"
              />
              <h3
                className="text-lg font-semibold"
                style={{ color: COLORS.primary }}
              >
                Investment growth over time
              </h3>
            </div>

            <div className="h-80">
              <Line data={generateChartData()} options={chartOptions} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-blue-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">
                  Total invested
                </p>
                <p
                  className="text-lg font-bold"
                  style={{ color: COLORS.accent }}
                >
                  <LocalizedCurrency
                    value={results.totalInvestment + results.lumpsumInvestment}
                  />
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">Final value</p>
                <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                  <LocalizedCurrency value={results.maturityValue} />
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl text-center">
              <p className="text-sm font-medium text-gray-600 mb-1">
                Wealth gained
              </p>
              <p className="text-2xl font-bold" style={{ color: COLORS.sage }}>
                <LocalizedCurrency value={results.estimatedReturns} />
              </p>
              <p className="text-sm text-gray-500">
                {(
                  (results.maturityValue /
                    (results.totalInvestment + results.lumpsumInvestment) -
                    1) *
                  100
                ).toFixed(1)}
                % growth •{' '}
                {(
                  results.maturityValue /
                  (results.totalInvestment + results.lumpsumInvestment)
                ).toFixed(2)}
                x multiplier
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mt-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
      >
        <div className="flex items-center mb-6">
          <FileText size={24} style={{ color: COLORS.gold }} className="mr-3" />
          <h3
            className="text-lg font-semibold"
            style={{ color: COLORS.primary }}
          >
            Year-wise investment breakdown
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th
                  className="text-left py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Period
                </th>
                {stepUpEnabled && (stepUpValue || 0) > 0 && (
                  <th
                    className="text-right py-3 px-2 font-semibold"
                    style={{ color: COLORS.charcoal }}
                  >
                    Monthly SIP
                  </th>
                )}
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  SIP investment
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Total SIP invested
                </th>
                {(lumpsumAmount || 0) > 0 && (
                  <>
                    <th
                      className="text-right py-3 px-2 font-semibold"
                      style={{ color: COLORS.charcoal }}
                    >
                      Lumpsum invested
                    </th>
                    <th
                      className="text-right py-3 px-2 font-semibold"
                      style={{ color: COLORS.charcoal }}
                    >
                      Lumpsum value
                    </th>
                    <th
                      className="text-right py-3 px-2 font-semibold"
                      style={{ color: COLORS.charcoal }}
                    >
                      Lumpsum gains
                    </th>
                  </>
                )}
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Total invested
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Portfolio value
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Total gains
                </th>
              </tr>
            </thead>
            <tbody>
              {generateTableData().map((row, index) => (
                <tr
                  key={`${row.year}-${index}`}
                  className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors duration-200 ${row.isInitial ? 'bg-blue-50 font-medium' : ''}`}
                >
                  <td
                    className="py-3 px-2 font-medium"
                    style={{ color: COLORS.primary }}
                  >
                    {row.isInitial ? 'Today (Start)' : `Year ${row.year}`}
                  </td>
                  {stepUpEnabled && (stepUpValue || 0) > 0 && (
                    <td className="py-3 px-2 text-right text-gray-700">
                      {row.isInitial ? (
                        '-'
                      ) : (
                        <LocalizedCurrency value={row.monthlyAmount} />
                      )}
                    </td>
                  )}
                  <td className="py-3 px-2 text-right text-gray-700">
                    {row.isInitial ? (
                      '-'
                    ) : (
                      <LocalizedCurrency value={row.yearlyInvestment} />
                    )}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    <LocalizedCurrency value={row.cumulativeSIPInvested} />
                  </td>
                  {(lumpsumAmount || 0) > 0 && (
                    <>
                      <td className="py-3 px-2 text-right text-gray-700">
                        <LocalizedCurrency value={row.lumpsumInvested} />
                      </td>
                      <td
                        className="py-3 px-2 text-right font-medium"
                        style={{ color: COLORS.sage }}
                      >
                        <LocalizedCurrency value={row.lumpsumValue} />
                      </td>
                      <td
                        className="py-3 px-2 text-right font-medium"
                        style={{
                          color:
                            row.lumpsumGains >= 0 ? COLORS.sage : '#ef4444',
                        }}
                      >
                        <LocalizedCurrency value={row.lumpsumGains} />
                      </td>
                    </>
                  )}
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: COLORS.accent }}
                  >
                    <LocalizedCurrency value={row.totalInvested} />
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: COLORS.sage }}
                  >
                    <LocalizedCurrency value={row.totalPortfolioValue} />
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{
                      color: row.totalGains >= 0 ? COLORS.sage : '#ef4444',
                    }}
                  >
                    <LocalizedCurrency value={row.totalGains} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Final investment
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.accent }}>
                <LocalizedCurrency
                  value={results.totalInvestment + results.lumpsumInvestment}
                />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Final portfolio value
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                <LocalizedCurrency value={results.maturityValue} />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total wealth gained
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                <LocalizedCurrency value={results.estimatedReturns} />
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200 text-xs">
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            {(() => {
              const definitions = [
                {
                  term: 'Period',
                  definition: 'Timeline of your investment journey',
                },
                ...(stepUpEnabled && (stepUpValue || 0) > 0
                  ? [
                      {
                        term: 'Monthly SIP',
                        definition:
                          'Your monthly investment amount for that year',
                      },
                    ]
                  : []),
                {
                  term: 'SIP investment',
                  definition: 'Money you put in that year',
                },
                {
                  term: 'Total SIP invested',
                  definition: 'All SIP money invested till that year',
                },
                ...((lumpsumAmount || 0) > 0
                  ? [
                      {
                        term: 'Lumpsum invested',
                        definition: 'One-time money (only at start)',
                      },
                      {
                        term: 'Lumpsum value',
                        definition: 'How your one-time investment grows',
                      },
                      {
                        term: 'Lumpsum gains',
                        definition: 'Profit on your one-time investment',
                      },
                    ]
                  : []),
                {
                  term: 'Total invested',
                  definition: 'All money you put in (SIP + Lumpsum)',
                },
                {
                  term: 'Portfolio value',
                  definition: 'Total worth of your investment',
                },
                {
                  term: 'Total gains',
                  definition: 'Extra wealth created (Portfolio - Invested)',
                },
              ];

              // Split definitions into two columns
              const midPoint = Math.ceil(definitions.length / 2);
              const firstColumn = definitions.slice(0, midPoint);
              const secondColumn = definitions.slice(midPoint);

              return (
                <>
                  <div>
                    <ul className="space-y-3">
                      {firstColumn.map((item, index) => (
                        <li key={index}>
                          <strong>{item.term}:</strong> {item.definition}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <ul className="space-y-3">
                      {secondColumn.map((item, index) => (
                        <li key={index}>
                          <strong>{item.term}:</strong> {item.definition}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </motion.div>

      <motion.div
        className="mt-16 max-w-5xl mx-auto px-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.1 }}
      >
        <article className="prose prose-lg md:prose-xl max-w-none">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
            What is SIP?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            <strong>Systematic Investment Plan (SIP)</strong> is like setting up
            an automatic transfer from your salary account to build wealth.
            Instead of trying to save a large amount once, you invest a small
            fixed amount every month - just like paying your EMI, but this EMI
            makes you richer!
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            An example
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            Think of SIP like buying groceries. Instead of spending ₹60,000 once
            a year on groceries, you spend ₹5,000 every month. Similarly,
            instead of investing ₹60,000 once, you invest ₹5,000 monthly in
            mutual funds.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Why SIP works
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            When markets are high, your ₹5,000 buys fewer units. When markets
            are low, the same ₹5,000 buys more units. Over time, you get a good
            average price - this is called <em>"Rupee Cost Averaging."</em>
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Important terms
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <p className="text-lg text-gray-700">
              <strong>Portfolio:</strong> Your investment collection - like your
              wardrobe but for money
            </p>
            <p className="text-lg text-gray-700">
              <strong>Returns:</strong> Profit you earn - like interest in bank
              but much higher
            </p>
            <p className="text-lg text-gray-700">
              <strong>Compounding effect:</strong> The longer you stay invested,
              the more your returns benefit from compounding
            </p>
            <p className="text-lg text-gray-700">
              <strong>Rupee cost averaging:</strong> This helps reduce the
              impact of market volatility
            </p>
            <p className="text-lg text-gray-700">
              <strong>Lumpsum:</strong> One-time big investment - like paying
              full house rent for a year
            </p>
            <p className="text-lg text-gray-700">
              <strong>Step-up SIP:</strong> Increasing monthly amount yearly -
              like salary increment
            </p>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            SIP vs Lumpsum investments
          </h3>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Mode of investment
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SIP:</strong> Regular monthly investments of a fixed amount,
            creating a disciplined investment habit.
            <br />
            <strong>Lumpsum:</strong> One-time investment of a large amount,
            requiring significant capital upfront.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Risk exposure
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SIP:</strong> Lower risk due to rupee cost averaging - you
            buy more units when prices are low and fewer when high.
            <br />
            <strong>Lumpsum:</strong> Higher risk as entire investment is
            exposed to market conditions at the time of investment.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Suitability
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SIP:</strong> Perfect for salaried individuals with regular
            income who want to build wealth gradually.
            <br />
            <strong>Lumpsum:</strong> Suitable for those with surplus funds,
            bonuses, or inheritance who can invest large amounts.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Compounding
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SIP:</strong> Benefits from compounding over time as each
            monthly investment gets time to grow.
            <br />
            <strong>Lumpsum:</strong> Maximum compounding benefit as the entire
            amount gets the full investment period to grow.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Flexibility
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SIP:</strong> High flexibility - can increase, decrease,
            pause, or stop anytime based on financial situation.
            <br />
            <strong>Lumpsum:</strong> Less flexible as it requires a large
            amount upfront and timing the market correctly.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Conclusion
          </h4>
          <p className="text-lg text-gray-700 mb-3 md:mb-4">
            <strong>SIP:</strong> Safer, disciplined approach perfect for
            regular income earners who want to build wealth systematically
            without worrying about market timing.
          </p>
          <p className="text-lg text-gray-700 mb-6 md:mb-8">
            <strong>Lumpsum:</strong> Higher potential returns if market timing
            is right and you have a long investment horizon, but requires more
            capital and market knowledge.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
            SIP vs traditional savings
          </h3>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Investment type
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">
                    Expected returns
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Characteristics
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Bank savings
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">3-4%</td>
                  <td className="py-3 px-4 text-gray-700">
                    Safe but inflation eats returns
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Fixed deposit (FD)
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">6-7%</td>
                  <td className="py-3 px-4 text-gray-700">
                    Safe but limited growth
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    SIP in equity mutual funds
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    12-15%*
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    Higher growth potential
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-500 mt-2 italic">
            *Historical average, not guaranteed
          </p>

          <p className="mt-6 md:mt-8">
            For more details, check out our guides on{' '}
            <a
              href="/guides/investing-in-mutual-funds"
              className="text-blue-600 hover:underline"
            >
              investing in mutual funds
            </a>{' '}
            and{' '}
            <a
              href="/guides/sip-investment-strategy"
              className="text-blue-600 hover:underline"
            >
              SIP investment strategy
            </a>
            .
          </p>
        </article>
      </motion.div>
    </ToolLayout>
  );
};
